/** Remember server waiting periods, with a same-tab fallback for older APIs. */
export const PASSWORD_ATTEMPTS_KEY = 'dohockey:password-login-attempts';
export const PASSWORD_WAIT_MS = 15 * 60 * 1000;
type Entry = { email: string; failures: number; expiresAt: number };
type AttemptStorage = Pick<Storage, 'getItem' | 'setItem'>;
const normalize = (email: string) => email.trim().toLowerCase();

export class PasswordLoginAttempts {
  private entries: Entry[] = [];
  constructor(private storage?: AttemptStorage) {
    try {
      const stored: unknown = JSON.parse(storage?.getItem(PASSWORD_ATTEMPTS_KEY) || '[]');
      if (Array.isArray(stored)) this.entries = stored.filter((item): item is Entry =>
        !!item && typeof item.email === 'string' && item.email.length <= 320 &&
        Number.isInteger(item.failures) && item.failures > 0 && item.failures <= 5 &&
        Number.isFinite(item.expiresAt)).slice(-20);
    } catch { /* Continue in memory when tab storage is unavailable. */ }
  }
  remainingSeconds(email: string, now = Date.now()): number {
    const entry = this.entries.find(item => item.email === normalize(email));
    return entry && entry.failures >= 5 ? Math.max(0, Math.min(900, Math.ceil((entry.expiresAt - now) / 1000))) : 0;
  }
  recordFailure(email: string, now = Date.now()): void {
    const normalized = normalize(email);
    if (!normalized || normalized.length > 320 || this.remainingSeconds(normalized, now)) return;
    this.entries = this.entries.filter(item => item.expiresAt > now);
    const previous = this.entries.find(item => item.email === normalized);
    this.entries = this.entries.filter(item => item.email !== normalized);
    this.entries.push({ email: normalized, failures: Math.min(5, (previous?.failures ?? 0) + 1), expiresAt: now + PASSWORD_WAIT_MS });
    this.entries = this.entries.slice(-20);
    this.persist();
  }
  recordServerWait(email: string, seconds: number, now = Date.now()): void {
    const normalized = normalize(email);
    if (!normalized || normalized.length > 320 || !Number.isFinite(seconds) || seconds <= 0) return;
    this.entries = this.entries.filter(item => item.email !== normalized && item.expiresAt > now);
    this.entries.push({ email: normalized, failures: 5, expiresAt: now + Math.min(900, Math.ceil(seconds)) * 1000 });
    this.entries = this.entries.slice(-20);
    this.persist();
  }
  clear(email: string): void {
    this.entries = this.entries.filter(item => item.email !== normalize(email));
    this.persist();
  }
  private persist(): void {
    try { this.storage?.setItem(PASSWORD_ATTEMPTS_KEY, JSON.stringify(this.entries)); }
    catch { /* The current page still enforces its UX waiting period. */ }
  }
}
